import { supabase } from '../lib/supabase';
import { EmergencyResource, CreateResourceData } from '../types/resource';

export class ResourceService {
  // Fetch all resources or filter by location
  static async fetchResources(filters?: {
    countryCode?: string;
    region?: string;
    city?: string;
    type?: string;
    status?: string;
  }): Promise<EmergencyResource[]> {
    try {
      let query = supabase
        .from('emergency_resources')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.countryCode) {
        query = query.eq('country_code', filters.countryCode);
      }
      if (filters?.region) {
        query = query.ilike('region', `%${filters.region}%`);
      }
      if (filters?.city) {
        query = query.ilike('city', `%${filters.city}%`);
      }
      if (filters?.type) {
        query = query.eq('type', filters.type);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching resources:', error);
      throw error;
    }
  }

  // Add a new resource
  static async addResource(resourceData: CreateResourceData): Promise<EmergencyResource> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw new Error('Authentication required to add resources');
      }

      const { data, error } = await supabase
        .from('emergency_resources')
        .insert({
          ...resourceData,
          user_id: user?.id
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error adding resource:', error);
      throw error;
    }
  }

  // Update an existing resource
  static async updateResource(id: string, updates: Partial<CreateResourceData>): Promise<EmergencyResource> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw new Error('Authentication required to update resources');
      }

      const { data, error } = await supabase
        .from('emergency_resources')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user?.id) // Only allow users to update their own resources
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error updating resource:', error);
      throw error;
    }
  }

  // Delete a resource
  static async deleteResource(id: string): Promise<void> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw new Error('Authentication required to delete resources');
      }

      const { error } = await supabase
        .from('emergency_resources')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id); // Only allow users to delete their own resources

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  }

  // Get resources near a specific location
  static async getResourcesNearLocation(
    latitude: number, 
    longitude: number, 
    radiusKm: number = 50
  ): Promise<EmergencyResource[]> {
    try {
      // Using PostGIS functions for geographic queries
      const { data, error } = await supabase.rpc('get_resources_near_location', {
        lat: latitude,
        lng: longitude,
        radius_km: radiusKm
      });

      if (error) {
        // Fallback to simple filtering if PostGIS function doesn't exist
        console.warn('PostGIS function not available, using simple filtering');
        return this.fetchResources();
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching nearby resources:', error);
      // Fallback to all resources
      return this.fetchResources();
    }
  }

  // Subscribe to real-time changes
  static subscribeToResourceChanges(
    callback: (payload: any) => void,
    filters?: { countryCode?: string; region?: string; city?: string }
  ) {
    let channel = supabase
      .channel('emergency_resources_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'emergency_resources'
        },
        callback
      );

    // Apply filters if provided
    if (filters?.countryCode) {
      channel = channel.filter('country_code', 'eq', filters.countryCode);
    }

    return channel.subscribe();
  }

  // Unsubscribe from real-time changes
  static unsubscribeFromResourceChanges(subscription: any) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }

  // Geocode an address to get coordinates
  static async geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
    try {
      // Using a free geocoding service
      const response = await fetch(
        `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(address)}&key=demo&limit=1`
      );
      
      if (!response.ok) {
        throw new Error('Geocoding failed');
      }
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        return {
          lat: result.geometry.lat,
          lng: result.geometry.lng
        };
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }
}